/**
 * Bot Error Panel Component
 * Enhanced error display with recovery suggestions
 */

import { getBotErrorSuggestion, getErrorSeverity } from '../../utils/bot-error-helper'

interface BotErrorPanelProps {
  errorMessage: string
  onRetry?: () => void
  onDismiss?: () => void
}

export function BotErrorPanel({ errorMessage, onRetry, onDismiss }: BotErrorPanelProps) {
  const suggestion = getBotErrorSuggestion(errorMessage)
  const severity = getErrorSeverity(errorMessage)

  const severityColors = {
    critical: {
      background: '#fee2e2',
      border: '#fecaca',
      icon: '🚨',
      iconBg: '#ef4444',
    },
    warning: {
      background: '#fef3c7',
      border: '#fde68a',
      icon: '⚠️',
      iconBg: '#f59e0b',
    },
    info: {
      background: '#dbeafe',
      border: '#bfdbfe',
      icon: 'ℹ️',
      iconBg: '#3b82f6',
    },
  }

  const colors = severityColors[severity]

  return (
    <div style={{ ...styles.container, backgroundColor: colors.background, borderColor: colors.border }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={{ ...styles.iconCircle, backgroundColor: colors.iconBg }}>
            <span style={styles.icon}>{colors.icon}</span>
          </div>
          <div>
            <h4 style={styles.category}>{suggestion.category}</h4>
            <p style={styles.description}>{suggestion.description}</p>
          </div>
        </div>
        {onDismiss && (
          <button onClick={onDismiss} style={styles.dismissButton} title="關閉">
            ✕
          </button>
        )}
      </div>

      {/* Suggestions */}
      <div style={styles.suggestions}>
        <h5 style={styles.suggestionsTitle}>💡 建議解決方案：</h5>
        <ul style={styles.suggestionsList}>
          {suggestion.suggestions.map((text, index) => (
            <li key={index} style={styles.suggestionItem}>
              {text}
            </li>
          ))}
        </ul>
      </div>

      {/* Actions */}
      {(suggestion.canRetry || onRetry) && (
        <div style={styles.actions}>
          {onRetry && suggestion.canRetry && (
            <button onClick={onRetry} style={styles.retryButton}>
              🔄 {suggestion.actionLabel || '重試'}
            </button>
          )}
        </div>
      )}

      {/* Original Error Message (collapsible) */}
      <details style={styles.details}>
        <summary style={styles.detailsSummary}>查看原始錯誤訊息</summary>
        <pre style={styles.errorText}>{errorMessage}</pre>
      </details>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    border: '1px solid',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    fontSize: '13px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  headerLeft: {
    display: 'flex',
    gap: '12px',
    flex: 1,
  },
  iconCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  icon: {
    fontSize: '16px',
  },
  category: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '4px',
  },
  description: {
    fontSize: '13px',
    color: '#666',
    margin: 0,
  },
  dismissButton: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    color: '#999',
    cursor: 'pointer',
    padding: '0',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
  },
  suggestions: {
    marginBottom: '12px',
  },
  suggestionsTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
  },
  suggestionsList: {
    margin: 0,
    paddingLeft: '20px',
  },
  suggestionItem: {
    fontSize: '13px',
    color: '#555',
    marginBottom: '6px',
    lineHeight: '1.5',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  retryButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  details: {
    marginTop: '8px',
    paddingTop: '12px',
    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
  },
  detailsSummary: {
    fontSize: '12px',
    color: '#666',
    cursor: 'pointer',
    fontWeight: '500',
  },
  errorText: {
    fontSize: '11px',
    color: '#999',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    padding: '8px',
    borderRadius: '4px',
    marginTop: '8px',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}
